/**
 * Route of a driving segment (fleet view, GET /v1/carrier/eld-events/{eventId}/route): which records have one and how
 * the line is coloured. The segment itself (its end, the positions, distance and stops) comes from the server.
 */
import type { Schemas } from '@/api/client';

export type RouteSegment = Schemas['RouteSegment'];
export type RouteKind = Schemas['RouteKind'];

/** Route kind of a record (driving, yard move, personal conveyance, unidentified driving), else null. */
export function routeKindOf(e: { eventType: number; eventCode: number; driverId: string | null }): RouteKind | null {
  if (e.eventType === 1 && e.eventCode === 3) return e.driverId ? 'driving' : 'unidentifiedDriving';
  if (e.eventType === 3 && e.eventCode === 2) return 'yardMove';
  if (e.eventType === 3 && e.eventCode === 1) return 'personalConveyance';
  return null;
}

/**
 * "View route" is offered on active records of a route kind that were recorded on a vehicle (the positions come from
 * that vehicle's ELD telemetry; without a vehicle there is no GPS data to show).
 */
export function canViewRoute(e: { eventType: number; eventCode: number; driverId: string | null; recordStatus: number; vehicleId?: string | null }): boolean {
  return e.recordStatus === 1 && !!e.vehicleId && routeKindOf(e) !== null;
}

export const ROUTE_KIND_LABEL: Record<RouteKind, string> = {
  driving: 'Driving',
  yardMove: 'Yard move',
  personalConveyance: 'Personal conveyance',
  unidentifiedDriving: 'Unidentified driving',
};

export type SpeedBand = 'route-slow' | 'route-mid' | 'route-fast';
export const SPEED_BANDS: { tone: SpeedBand; label: string }[] = [
  { tone: 'route-slow', label: 'Under 25 mph' },
  { tone: 'route-mid', label: '25–55 mph' },
  { tone: 'route-fast', label: 'Over 55 mph' },
];

export function speedBand(mph: number | null): SpeedBand {
  if (mph === null) return 'route-mid';
  return mph < 25 ? 'route-slow' : mph <= 55 ? 'route-mid' : 'route-fast';
}

/** Consecutive points of the same speed band as lines (each run shares its first point with the previous run's end). */
export function speedRuns(points: RouteSegment['points']): { tone: SpeedBand; points: [number, number][] }[] {
  const runs: { tone: SpeedBand; points: [number, number][] }[] = [];
  points.forEach((p, i) => {
    const tone = speedBand(p.speedMph);
    const last = runs.at(-1);
    if (last && last.tone === tone) last.points.push([p.lon, p.lat]);
    else {
      const prev = i > 0 ? points[i - 1]! : null;
      runs.push({ tone, points: prev ? [[prev.lon, prev.lat], [p.lon, p.lat]] : [[p.lon, p.lat]] });
    }
  });
  return runs.filter((r) => r.points.length >= 2);
}

/** "2 h 05 min" / "12 min" from seconds. */
export function formatDurationSec(sec: number): string {
  const m = Math.round(Math.max(0, sec) / 60);
  const h = Math.floor(m / 60);
  return h ? `${h} h ${String(m % 60).padStart(2, '0')} min` : `${m} min`;
}
